import { Fathom, FP, RxOperators } from "../deps"
import * as Events from "../events/events"
import * as ShopifyService from "../service-shopify/service-shopify"
import * as SellalongService from "../service-sellalong/service-sellalong"

// Temporary variable for flag global enabled state
let uiEnabled: boolean = false

export function setUiEnabled(value: boolean) {
  uiEnabled = value
}

type Config = Record<
  ShopifyService.ShopId,
  {
    fathom: {
      id: string
      checkoutSellalongCredit: string
      checkoutSellalongTotal: string
      customerCheckoutCta: string
      customerPanelImpression: string
      guestCheckoutCta: string
      guestPanelImpression: string
      loginCta: string
      sellalongSelected: string
    }
  }
>

const config: Config = {
  [ShopifyService.ShopId.SellalongDevelopment]: {
    fathom: {
      id: "KFWYIMLQ",
      checkoutSellalongCredit: "SX5HB2MB",
      checkoutSellalongTotal: "D1LQ8Y4V",
      customerCheckoutCta: "9AZXD5T9",
      customerPanelImpression: "ZVSEBS1G",
      guestCheckoutCta: "JIPLCVN6",
      guestPanelImpression: "YQ3DZYG6",
      loginCta: "LWA33ECA",
      sellalongSelected: "YER5XSQF",
    },
  },
  [ShopifyService.ShopId.BuyMeOnce]: {
    fathom: {
      id: "NKPNOFYJ",
      checkoutSellalongCredit: "0ZAKZGCB",
      checkoutSellalongTotal: "8LSZ3CDL",
      customerCheckoutCta: "WD8UBC53",
      customerPanelImpression: "LHVHTXCA",
      guestCheckoutCta: "YK8TSZFJ",
      guestPanelImpression: "YF0KCH2D",
      loginCta: "VYSGJCCL",
      sellalongSelected: "IXM14DEG",
    },
  },
  [ShopifyService.ShopId.Sirplus]: {
    fathom: {
      id: "MJLIYRMB",
      checkoutSellalongCredit: "DUQYFBEQ",
      checkoutSellalongTotal: "1C5HDUZ9",
      customerCheckoutCta: "XL9WLUIT",
      customerPanelImpression: "STCCLZOO",
      guestCheckoutCta: "PAQWPFQF",
      guestPanelImpression: "TRQC5KAR",
      loginCta: "XVXAJHW9",
      sellalongSelected: "EPYE2NAS",
    },
  },
}

// @ts-ignore
const trackWithFathom: (
  shopId: ShopifyService.ShopId
) => (event: Events.Event) => void = (shopId) => {
  const { fathom } = config[shopId]
  Fathom.load(fathom.id, {
    canonical: false,
    honorDNT: false,
    spa: "hash",
    url: "https://beaver.sellalong.io/script.js",
  })

  return (event) => {
    const hasCustomer = SellalongService.hasCustomer()
    const checkoutTotal = SellalongService.getCartTotal().value * 100
    const sellalongDiscountTotal =
      SellalongService.getSellalongDiscountTotal() * 100
    const sellalongCartTotal =
      SellalongService.getCartTotalAfterDiscount().value * 100

    const sitePath = window.location.pathname

    switch (event.kind) {
      case "checkoutIntent": {
        Fathom.trackPageview({ url: "/checkout" })
        Fathom.trackGoal(
          hasCustomer ? fathom.customerCheckoutCta : fathom.guestCheckoutCta,
          checkoutTotal
        )
        Fathom.trackGoal(fathom.checkoutSellalongCredit, sellalongDiscountTotal)
        Fathom.trackGoal(fathom.checkoutSellalongTotal, sellalongCartTotal)
        return
      }
      case "loginCtaIntent":
        Fathom.trackPageview({ url: "/account/login" })
        Fathom.trackGoal(fathom.loginCta, 0)
        return
      case "panelImpressionAction":
        Fathom.trackGoal(
          hasCustomer
            ? fathom.customerPanelImpression
            : fathom.guestPanelImpression,
          0
        )
        return
      case "purchaseStateTransitionAction":
        Fathom.trackGoal(fathom.sellalongSelected, 0)
        return
      case "sellalongBrandCtaIntent":
        Fathom.trackPageview({ url: `${sitePath}#outbound-sellalong.com" ` })
        return
      case "sellalongFaqCtaIntent":
        Fathom.trackPageview({
          url: `${sitePath}#outbound-sellalong.com/faq" `,
        })
        return
    }
  }
}

const trackWithSellalong: (
  shopId: ShopifyService.ShopId
) => (event: Events.Event) => void = (shopId) => (event) => {
  const sitePath = window.location.pathname
  const common = {
    eventKind: event.kind,
    shopId,
    sitePath,
    uiEnabled,
  }

  switch (event.kind) {
    case "cartChangeAction":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return

    case "cartImpressionAction":
      SellalongService.recordEvent({
        ...common,
        eventState: event.fullPage ? "FULLPAGE" : "OVERLAY",
        eventTarget: null,
      })()
      return

    case "checkoutIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return

    case "loginStartIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return

    case "loginCtaIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
        sitePath: "/account/login",
      })()
      return

    case "panelImpressionAction":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return

    case "purchaseStateTransitionAction":
      SellalongService.recordEvent({
        ...common,
        eventState: event.currentState,
        eventTarget: event.purchaseId,
      })()
      return

    case "sellalongCtaIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return

    case "sellalongBrandCtaIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
        sitePath: `${sitePath}#outbound-sellalong.com`,
      })()
      return

    case "sellalongFaqCtaIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
        sitePath: `${sitePath}#outbound-sellalong.com/faq`,
      })()
      return

    case "helpModalShowIntent":
      SellalongService.recordEvent({
        ...common,
        eventState: null,
        eventTarget: null,
      })()
      return
  }
}

export function init(shopId: ShopifyService.ShopId) {
  return FP.function
    .pipe(
      Events.eventBus,
      // Fathom tracking disabled 21 March 2022
      // RxOperators.tap(trackWithFathom(shopId)),
      RxOperators.tap(trackWithSellalong(shopId))
    )
    .subscribe()
}
